import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { isLoggedIn, selectToken } from "../login/loginSlice";
import { changeSelectedBranch, loadAccountsByBank, selectAccounts, selectCurrentBranch } from "../manageAccounts/accountsSlice";
import { addTransaction, addTransactionPending, clearNewTransaction } from "../transactionList/transactionsSlice";
import { resetTransferForm, selectFromError, selectToError, selectTransferAmount, selectTransferFormStatus, selectTransferFrom, selectTransferTo, selectValidAmountCheck, setTransferAmount, setTransferConfirming, setTransferFrom, setTransferTo, transferIsConfirming } from "./transferSlice";

import './Transfer.css'

import Sidebar from "../../components/sidebar/Sidebar";
import { loadBranches, selectBranches } from "../manageBranches/branchesSlice";
import { selectUser } from "../home/userSlice";
import Select from "react-select";

export default function Transfer() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const loggedIn = useSelector(isLoggedIn);
    const token = useSelector(selectToken);
    
    const transferFrom = useSelector(selectTransferFrom);
    const transferTo = useSelector(selectTransferTo);
    const transferAmount = useSelector(selectTransferAmount);
    const fromError = useSelector(selectFromError);
    const toError = useSelector(selectToError);
    const transferFormStatus = useSelector(selectTransferFormStatus);
    const validAmountCheck = useSelector(selectValidAmountCheck);
    
    const branches = useSelector(selectBranches);
    const selectedBranch = useSelector(selectCurrentBranch);
    
    const accounts = useSelector(selectAccounts);

    let transferFromAccountsOptions = [{
        value: "blank", label: "-- select --"
    }]
    Object.values(accounts).forEach(account => {
        if ((selectedBranch === 'blank' || selectedBranch === account.accountId) && transferTo.value !== account.accountId) {
            transferFromAccountsOptions.push({
                value: account.accountId,
                label: `${account.username} | ${account.accountType} | ${account.balance.toFixed(2)}`
            })
        }
    })

    let transferToAccountsOptions = [{
        value: "blank", label: "-- select --"
    }]
    Object.values(accounts).forEach(account => {
        if ((selectedBranch === 'blank' || selectedBranch === account.accountId) && transferFrom.value !== account.accountId) {
            transferToAccountsOptions.push({
                value: account.accountId,
                label: `${account.username} | ${account.accountType} | ${account.balance.toFixed(2)}`
            })
        }
    })

    const addingTransaction = useSelector(addTransactionPending);
    const confirmingTransfer = useSelector(transferIsConfirming);

    const userInfo = useSelector(selectUser);
    const userRole = userInfo.userRole;
    const bankId = userInfo.bankId;

    useEffect(() => {
        if (!loggedIn) {
            navigate(`../login`);
        }
    }, [loggedIn, navigate])

    useEffect(() => {
        if (userRole !== 'USER') {
            dispatch(loadBranches({bankId, token}));
            dispatch(loadAccountsByBank({bankId, token}));
        }
    }, [userRole, token, bankId, dispatch])

    useEffect(() => {
        dispatch(setTransferConfirming(false));
        dispatch(resetTransferForm());
        dispatch(clearNewTransaction());
    }, [dispatch]);

    const handleSelectedBranchChange = event => {
        dispatch(changeSelectedBranch(event.target.value));
    }

    const handleTransferFromChange = option => {
        dispatch(setTransferFrom(option));
    }

    const handleTransferToChange = option => {
        dispatch(setTransferTo(option));
    }

    const handleTransferAmountChange = event => {
        const newTransferAmount = event.target.value;
        const transferLimit = accounts[transferFrom.value] ? accounts[transferFrom.value].balance : 0;
        
        const payload = {
            transferAmount: newTransferAmount,
            validAmountCheck: Number(newTransferAmount) < transferLimit && typeof Number(newTransferAmount) === "number" && Number(newTransferAmount) > 0
        }

        dispatch(setTransferAmount(payload));
    }

    const handleTransferButtonClick = event => {
        event.preventDefault();
        dispatch(setTransferConfirming(true));
    }

    const handleCancelButtonClick = event => {
        event.preventDefault();
        dispatch(setTransferConfirming(false));
    }

    const handleSubmit = event => {
        event.preventDefault();

        dispatch(setTransferFrom(transferFrom));
        dispatch(setTransferTo(transferTo));

        if (transferFrom.value !== "blank") {
            dispatch(setTransferAmount({
                transferAmount: transferAmount,
                validAmountCheck: Number(transferAmount) < accounts[transferFrom.value].balance && typeof Number(transferAmount) === "number" && Number(transferAmount) > 0
            }));
        };
        

        if (transferFrom.value !== "blank" && transferTo.value !== "blank" && transferAmount < accounts[transferFrom.value].balance) {
            const date = new Date();
            const year = date.getFullYear();
            const month = date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1;
            const day = date.getDate() + 1 < 10 ? `0${date.getDate() + 1}` : date.getDate() + 1;
            const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
            const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
            const seconds = date.getSeconds() < 10 ? `0${date.getSeconds()}` : date.getSeconds();
            const transactionDate = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

            const fromDescription = userRole === 'USER' ? 
                `Transfer to ${accounts[transferTo.value].accountType} **${accounts[transferTo.value].accountId.slice(-4)}` 
                : `Transfer to ${accounts[transferTo.value].username} ${accounts[transferTo.value].accountType} **${accounts[transferTo.value].accountId.slice(-4)}`
            const fromTransaction = {
                accountId: transferFrom.value,
                type: 'debit',
                description: fromDescription,
                amount: transferAmount,
                transactionDate: transactionDate,
            }
            dispatch(addTransaction({transaction: fromTransaction, token}));

            const toDescription = userRole === 'USER' ? 
                `Transfer from ${accounts[transferFrom.value].accountType} **${accounts[transferFrom.value].accountId.slice(-4)}` 
                : `Transfer from ${accounts[transferTo.value].username} ${accounts[transferTo.value].accountType} **${accounts[transferTo.value].accountId.slice(-4)}`
            const toTransaction = {
                accountId: transferTo.value,
                type: 'credit',
                description: toDescription,
                amount: transferAmount,
                transactionDate: transactionDate
            }
            dispatch(addTransaction({transaction: toTransaction, token}));

            navigate(`../${bankId}/transfer/confirmation`);
        }
    }

    return (
        <main>
            <Sidebar />
            <section className="transfer">
                <h2>{confirmingTransfer ? 'Confirm Transfer' : 'Transfer Money'}</h2>
                {userRole === 'USER' ? "" : 
                <div>
                    {confirmingTransfer ? "" :
                        <label>
                            <h3>Select Branch</h3>
                            <select name="select-branch" onChange={handleSelectedBranchChange} value={selectedBranch}>
                                <option id="blank" key="blank" value="blank">-- select --</option>
                                {branches.map(branch => {
                                    return <option id={branch.branchId} key={branch.branchId} value={branch.branchId}>{branch.branchName}</option>
                                })}
                            </select>
                        </label>}
                </div>
                }
                <form id="transfer">
                    {confirmingTransfer ? <p><span>Transfer From:</span> {Object.values(accounts).map(account => account.accountId === transferFrom.value ? `${account.accountType} ${account.accountId}` : "")}</p> : 
                        <label>
                            <h3>Transfer From</h3>
                            <Select 
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={transferFrom}
                                onChange={handleTransferFromChange}
                                options={transferFromAccountsOptions}
                                placeholder="Select an account..."
                                unstyled={true}
                            />
                            {fromError ? <p className="error">Please select a valid account</p> : ""}
                        </label>}
                    {confirmingTransfer ? <p><span>Transfer To:</span> {Object.values(accounts).map(account => account.accountId === transferTo.value ? `${account.accountType} ${account.accountId}` : "")}</p> : 
                        <label>
                            <h3>Transfer To</h3>
                            <Select 
                                className="react-select-container"
                                classNamePrefix="react-select"
                                value={transferTo}
                                onChange={handleTransferToChange}
                                options={transferToAccountsOptions}
                                placeholder="Select an account..."
                                unstyled={true}
                            />
                            {toError ? <p className="error">Please select  a valid account</p> : ""}
                        </label>}
                    {confirmingTransfer ? <p><span>Transfer Amount:</span> ${Number(transferAmount).toFixed(2)}</p> : 
                        <label>
                            <h3>Amount</h3>
                            <div className="amount-field">
                                <p className="dollar-sign">$</p>
                                <input name="amount" onChange={handleTransferAmountChange} value={transferAmount}></input>
                            </div>
                            {!validAmountCheck ? <p className="error">Please enter a valid amount</p> : ""}
                        </label>}
                    <p className={transferFormStatus === 'Transfer successfully submitted' ? "confirmation" : "error"}>{transferFormStatus}</p>
                    <div className="button-container">
                        {confirmingTransfer ? <button onClick={handleCancelButtonClick}>Cancel</button> : ""}
                        {!confirmingTransfer ? <button onClick={handleTransferButtonClick}>Transfer Money</button> : <button onClick={handleSubmit} disabled={addingTransaction}>{addingTransaction ? "Processing Transfer..." : "Submit Transfer"}</button>}
                    </div>
                </form>
            </section>
        </main>
    )
}